/**
 * Day 36 — Mobile Clinical Chart Menu
 * Lists enabled clinical charts for a selected participant. Support workers
 * only ever see participants assigned to them (enforced server-side).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { fetchAllPages } from '@/lib/api';
import SearchSelect from '@/components/common/SearchSelect';
import { participantLabel, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft, ClipboardList, Activity, Droplet, Moon, Brain, Zap, ThermometerSun, Bandage, ChevronRight,
} from 'lucide-react';
import MobileAccessDenied from '@/components/mobile/MobileAccessDenied';

const ICONS = { Activity, Droplet, Moon, Brain, Zap, ThermometerSun, Bandage };

export default function MobileChartsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shiftId = searchParams.get('shiftId') || '';

  const [participants, setParticipants] = useState([]);
  const [participantId, setParticipantId] = useState(searchParams.get('participantId') || '');
  const [charts, setCharts] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    fetchAllPages('/participants')   // NEW-007: every assigned participant, not just the first 100
      .then(items => setParticipants(items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!participantId) { setCharts([]); return; }
    setLoading(true);
    api.get('/mobile/charts/menu', { params: { participantId } })
      .then(res => {
        setCharts(res.data?.data || []);
        setEnabled(res.data?.enabled !== false);
      })
      .catch(err => { if (err.response?.status === 403) setAccessDenied(true); })
      .finally(() => setLoading(false));
  }, [participantId]);

  const openChart = (code) => {
    const params = new URLSearchParams({ participantId, type: code });
    if (shiftId) params.set('shiftId', shiftId);
    navigate(`/mobile/charts/entry?${params.toString()}`);
  };

  return (
    <>
      {accessDenied && <MobileAccessDenied />}
      <div className={`min-h-screen bg-background pb-24 ${accessDenied ? 'hidden' : ''}`} data-testid="mobile-charts-page">
        <div className="sticky top-0 z-10 bg-primary text-primary-foreground px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary/80" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                <h1 className="text-lg font-bold">Clinical Charts</h1>
              </div>
              <p className="text-xs opacity-80">Record NDIS &amp; aged-care observations</p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="participant">Participant</Label>
            <SearchSelect
              options={participants.map(p => ({ value: p.id, label: participantLabel(p), keywords: p.ndis_number || '' }))}
              value={participantId}
              onChange={setParticipantId}
              placeholder="Select participant"
              searchPlaceholder={PARTICIPANT_SEARCH_HINT}
              emptyText="No participants match that search"
              className="h-11"
              testId="charts-participant-select"
            />
          </div>

          {!enabled && participantId && (
            <p className="text-sm text-muted-foreground" data-testid="charts-disabled">
              Clinical charts are not enabled for your organisation.
            </p>
          )}

          {loading && <p className="text-sm text-muted-foreground">Loading charts…</p>}

          {participantId && enabled && !loading && charts.length === 0 && (
            <p className="text-sm text-muted-foreground">No charts are enabled.</p>
          )}

          <div className="grid grid-cols-2 gap-3" data-testid="charts-menu-grid">
            {charts.map(c => {
              const Icon = ICONS[c.icon] || ClipboardList;
              return (
                <button
                  key={c.code}
                  onClick={() => openChart(c.code)}
                  className="flex flex-col items-start gap-2 rounded-xl border bg-card p-4 text-left transition-all hover:border-primary hover:bg-primary/5"
                  data-testid={`chart-tile-${c.code}`}
                >
                  <div className="flex w-full items-center justify-between">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-sm leading-tight">{c.name}</p>
                    <p className="text-[11px] text-muted-foreground">{c.category}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
